/* -------------------------- Design imports start -------------------------- */
import { Box, CircularProgress } from '@mui/material'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import {
  type ModelTree,
  type ModelTreeNodeClickEvent,
  type ModelTreeNodeVisibilityClickEvent,
} from '@hoops/web-viewer-components'
import * as Communicator from '@hoops/web-viewer'
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import LogTool from '../../logger/logTools'
import { fetchS3Object } from '../../utils/functions'
import {
  getLeafNodeIds,
  getModelTree,
  getNonLeafNodeIds,
  toCanonicalPartName,
} from '../../features/UnauthRequest/utils/tree-functions'
import { Part, type Article } from '../../features/Item/utils/types'
import { type FileInfo } from '../../utils/types'
import { type APIArticle } from '../../generated-types/models/APIArticle'
import { createArticle, createPart } from '../../features/Item/utils/functions'
import { usePartChildren } from '../../features/Bom/hooks/usePartChildren'
import { type Model, type NavCube, type View } from '../../../local_modules/@hoops/communicator/25.2.0/web-viewer'
// eslint-disable-next-line import/no-unresolved
import { type SelectionManager } from '../../../local_modules/@hoops/communicator/25.2.0/web-viewer/lib/selection'
import HoopsCADViewerSidebar from './HoopsCADViewerSidebar'
import HoopsCADViewerToolbar from './HoopsCADViewerToolbar'
import { type HoopsCADViewerProps, type ViewerSetting } from './types'
/* ------------------------- Functional imports end ------------------------- */

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function HoopsCADViewer(props: HoopsCADViewerProps): ReactElement {
  const {
    file,
    rootArticle,
    requestSelf,
    accessSecret,
    authHeaders,
    showSidebar = true,
    showToolbar = true,
    modelName = '',
    onSelectionChange,
    onNodeClick,
    onVisibilityChange,
    onMappingsReady,
    containerId = 'hoops-cad-viewer',
    enginePath = '../js/hoops/25.2.0',
    toolbarPosition,
    onViewerReady,
    onModelStructureReady,
    onSceneReady,
    onPartChildrenLoadingChange,
  } = props

  /* -------------------------- Non state data start -------------------------- */
  const log = new LogTool({ context: 'HoopsCADViewer', enable: true, logLevel: 'debug' })
  const { t } = useTranslation()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [isDOMReady, setIsDOMReady] = useState(false)
  const [isViewerReady, setIsViewerReady] = useState(false)
  const [openCADViewerSettings, setOpenCADViewerSettings] = useState(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const hwv = useRef<Communicator.WebViewer | null>(null)
  const modelTree = useRef<ModelTree | null>(null)
  const [selectedNodeIds, setSelectedNodeIds] = useState<Communicator.BodyId[]>([])
  const [cadViewerSettingsState, setSettings] = useState<string[]>(['showEdges', 'antiAliasing'])

  // Assembly support: leaf/non-leaf node mappings (always managed internally)
  const leafNodesDatasource = useRef<Communicator.BodyId[]>([])
  const leafIdPartMapping = useRef<{ [id: string]: Part }>({})
  const leafNamePartMapping = useRef<{ [canonical: string]: Part }>({})
  const nonLeafNodesDatasource = useRef<Communicator.BodyId[]>([])
  const nonLeafIdPartMapping = useRef<{ [id: string]: Part }>({})
  const nonLeafNamePartMapping = useRef<{ [canonical: string]: Part }>({})
  const isPollingChildren = useRef(false)
  const lastChildrenFetchFingerprintRef = useRef<string>('')

  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    setIsDOMReady(true)
  }, [])

  // Handle window resizing -> HWV needs to be resized as well
  useEffect(() => {
    const resizeHWVCanvas = (): void => {
      if (hwv.current !== null) {
        hwv.current.resizeCanvas()
      }
    }
    window.addEventListener('resize', resizeHWVCanvas)
    return () => {
      window.removeEventListener('resize', resizeHWVCanvas)
    }
  }, [])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  const notifyMappingsReady = useCallback((): void => {
    if (onMappingsReady) {
      onMappingsReady({
        leafNodesDatasource,
        leafIdPartMapping,
        leafNamePartMapping,
        nonLeafNodesDatasource,
        nonLeafIdPartMapping,
        nonLeafNamePartMapping,
      })
    }
  }, [
    leafIdPartMapping,
    leafNamePartMapping,
    leafNodesDatasource,
    nonLeafIdPartMapping,
    nonLeafNamePartMapping,
    nonLeafNodesDatasource,
    onMappingsReady,
  ])

  const syncSingleLeafMapping = useCallback(
    (article: Part | null | undefined): boolean => {
      if (!article) return false
      if (leafNodesDatasource.current.length !== 1) return false
      const onlyLeafId = leafNodesDatasource.current[0]
      if (typeof onlyLeafId !== 'number') return false
      const leafKey = String(onlyLeafId)
      const current = leafIdPartMapping.current[leafKey]
      if (current === article) return false
      leafIdPartMapping.current[leafKey] = article
      const canonical = toCanonicalPartName(article.name || '')
      if (canonical) {
        leafNamePartMapping.current[canonical] = article
      }
      return true
    },
    [leafIdPartMapping, leafNamePartMapping, leafNodesDatasource]
  )

  const handlePartChildrenNode = useCallback(
    (_parent: APIArticle, children: APIArticle[]) => {
      createPartMappings(_parent)
      if (children.length === 0) {
        log.warn('No children found for', _parent.name, _parent.number)
        return
      }
      children.forEach((apiArticle: APIArticle) => {
        const part: Part = createPart(apiArticle)
        const hpId = part.hoopsPartId
          ? String(part.hoopsPartId)
          : part.number
            ? String(part.number)
            : undefined
        if (!hpId) {
          log.warn('Child article without hoopsPartId or number', apiArticle)
          return
        }
        const numericId = Number(hpId)
        const isLeaf = leafNodesDatasource.current.includes(numericId)
        if (isLeaf) {
          leafIdPartMapping.current[hpId] = part
        } else {
          nonLeafIdPartMapping.current[hpId] = part
        }
        const canonical = toCanonicalPartName(part.name || '')
        if (canonical) {
          if (isLeaf) {
            leafNamePartMapping.current[canonical] = part
          } else {
            nonLeafNamePartMapping.current[canonical] = part
          }
        }
      })
    },
    [
      createPartMappings,
      leafNodesDatasource,
      leafIdPartMapping,
      leafNamePartMapping,
      nonLeafIdPartMapping,
      nonLeafNamePartMapping,
    ]
  )

  const handlePartChildrenFinal = useCallback(() => {
    const currentMapped = Object.keys(leafIdPartMapping.current).length
    log.info('Finished children fetch. mapped ->', currentMapped)
    isPollingChildren.current = false
    notifyMappingsReady()
  }, [notifyMappingsReady])

  const handlePartChildrenError = useCallback(
    (error: any) => {
      log.error('Error fetching children for CAD model:', error)
      toast.error(t('cadFileViewer:feedback.error.loadingAssemblyFeedback'))
      isPollingChildren.current = false
    },
    [t]
  )

  const { loadChildren, isLoading: isFetchingPartChildren } = usePartChildren({
    requestSelf,
    accessSecret,
    authHeaders,
    onNode: handlePartChildrenNode,
    onFinal: handlePartChildrenFinal,
    onError: handlePartChildrenError,
  })

  const childrenRequestFingerprint = useMemo(() => {
    const authFingerprint =
      authHeaders && Object.keys(authHeaders).length > 0
        ? JSON.stringify(authHeaders)
        : accessSecret
          ? `access:${accessSecret}`
          : ''
    const rootKey = rootArticle?.self ?? ''
    return `${authFingerprint}|${rootKey}`
  }, [accessSecret, authHeaders, rootArticle?.self])

  useEffect(() => {
    if (onPartChildrenLoadingChange) {
      onPartChildrenLoadingChange(isFetchingPartChildren)
    }
  }, [isFetchingPartChildren, onPartChildrenLoadingChange])

  useEffect(() => {
    if (!rootArticle) return
    if (!isViewerReady) return
    if (leafNodesDatasource.current.length <= 1) return
    if (isFetchingPartChildren) return
    if (childrenRequestFingerprint === lastChildrenFetchFingerprintRef.current) return
    pollPartChildren(rootArticle)
  }, [childrenRequestFingerprint, isFetchingPartChildren, isViewerReady, rootArticle])

  useEffect(() => {
    if (!rootArticle) return
    if (!isViewerReady) return
    if (syncSingleLeafMapping(rootArticle)) {
      notifyMappingsReady()
    }
  }, [isViewerReady, notifyMappingsReady, rootArticle, syncSingleLeafMapping])

  function createPartMappings(part: APIArticle | Article): void {
    const article: Part = createPart(part)
    let hpId: string | undefined
    if (article.hoopsPartId) {
      hpId = String(article.hoopsPartId)
    } else {
      hpId = String(article.number)
    }
    if (!hpId) {
      log.warn('Parent article without hoopsPartId', article)
      return
    }
    const numericId = Number(hpId)
    if (leafNodesDatasource.current.includes(numericId)) {
      // Leaf node mapping
      leafIdPartMapping.current[hpId] = article
    } else {
      // Non-leaf (internal) node mapping, including root
      nonLeafIdPartMapping.current[hpId] = article
    }
    const canonical = toCanonicalPartName(article.name || '')
    if (canonical) {
      if (leafNodesDatasource.current.includes(numericId)) {
        if (typeof leafNamePartMapping.current[canonical] === 'undefined') {
          leafNamePartMapping.current[canonical] = article
        }
      } else {
        if (typeof nonLeafNamePartMapping.current[canonical] === 'undefined') {
          nonLeafNamePartMapping.current[canonical] = article
        }
      }
    }
    syncSingleLeafMapping(article)
  }

  function pollPartChildren(part: Part): void {
    if (!rootArticle) {
      log.warn('Cannot poll children: rootArticle not provided')
      return
    }
    if (isFetchingPartChildren) {
      log.debug('Children fetch already in progress')
      return
    }
    if (childrenRequestFingerprint === lastChildrenFetchFingerprintRef.current) {
      return
    }
    lastChildrenFetchFingerprintRef.current = childrenRequestFingerprint
    isPollingChildren.current = true
    void loadChildren(part)
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleSceneReady = async (scsUrl: string): Promise<void> => {
    log.info('Scene is ready')
    // Initialize NavCube and Axis Triad for better navigation
    try {
      // Enable NavCube
      const hoopsWebViewer: Communicator.WebViewer | null = hwv.current
      const view: View | undefined = hoopsWebViewer?.view as unknown as View | undefined
      const navCube: NavCube | undefined = view?.navCube as unknown as NavCube | undefined
      if (!navCube) throw new Error('Navigation cube not defined')
      await navCube.enable()
      await navCube.setAnchor(Communicator.OverlayAnchor.LowerLeftCorner)
      navCube.setPreserveModelUp(true) // Keep model upright during navigation

      log.info('NavCube enabled successfully')
    } catch (error) {
      log.error('Error enabling NavCube:', error)
    }

    // Get file URL - support both FileInfo and direct URL strings
    const fileUrl = typeof file === 'string' ? file : (file as FileInfo)?.document
    if (!fileUrl) {
      log.error('No file URL available')
      return
    }

    // Check if it's an S3 URL (starts with http/https) or direct URL
    if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
      // S3 URL - fetch it
      await fetchS3Object({
        url: fileUrl,
        onSuccess: (fileBlob: Blob): void => {
          const blobUrl = URL.createObjectURL(fileBlob)
          void (async () => { await loadModelFromUrl(blobUrl, true) })()
        },
        onError: (error: any) => {
          log.error('Error fetching S3 object', error)
          toast.error(t('cadFileViewer:feedback.error.loading3DModel'))
        },
      })
    } else {
      // Direct URL - load directly
      await loadModelFromUrl(fileUrl, false)
    }

    // Configure selection colors
    const primary = new Communicator.Color(22, 169, 228)
    const hoopsWebViewer: Communicator.WebViewer | null = hwv.current
    if (!hoopsWebViewer) throw new Error('Hoops Web Viewer is not defined')
    const selectionManager: SelectionManager = hoopsWebViewer.selectionManager as unknown as SelectionManager
    await selectionManager.setNodeElementSelectionHighlightMode(
      Communicator.SelectionHighlightMode.HighlightOnly
    )
    await selectionManager.setNodeSelectionHighlightMode(Communicator.SelectionHighlightMode.HighlightOnly)
    await selectionManager.setNodeElementSelectionColor(primary)
    await selectionManager.setNodeSelectionColor(primary)

    if (onSceneReady && hwv.current) {
      onSceneReady(hwv.current)
    }
  }

  const loadModelFromUrl = async (fileUrl: string, shouldRevoke: boolean): Promise<void> => {
    const hoopsWebViewer: Communicator.WebViewer | null = hwv.current
    const model: Model | undefined = hoopsWebViewer?.model as unknown as Model | undefined
    if (!model) throw new Error('Model not defined')
    const rootNodeId = model.getAbsoluteRootNode()

    await model.loadSubtreeFromScsFile(rootNodeId, fileUrl)
    if (shouldRevoke) {
      URL.revokeObjectURL(fileUrl) // Clean up the object URL
    }

    // Extract model outline
    const modelTreeSnapshot = await getModelTree(hwv)
    log.debug('modelTree ->', modelTreeSnapshot)

    // Re-bind model to ModelTree AFTER subtree has fully loaded
    if (modelTree.current) {
      modelTree.current.model = model
    }

    // Recompute leaf nodes after the SCS subtree has been loaded
    leafNodesDatasource.current = getLeafNodeIds(hwv)
    nonLeafNodesDatasource.current = getNonLeafNodeIds(hwv)

    // Notify about initial mappings (even if empty, so external components know the structure is ready)
    notifyMappingsReady()

    // Start polling children if it's an assembly and rootArticle is provided
    if (leafNodesDatasource.current.length > 1 && rootArticle && !isPollingChildren.current) {
      pollPartChildren(rootArticle)
    }

    // Visual feedback
    setIsViewerReady(true)

    if (onViewerReady && hwv.current) {
      onViewerReady(hwv.current)
    }
  }

  const handleModelStructureReady = (): void => {
    log.info('Model structure is ready')
    if (modelTree.current) {
      const hoopsWebViewer: Communicator.WebViewer | null = hwv.current
      if (!hoopsWebViewer) throw new Error('Hoops Web Viewer is not defined')
      modelTree.current.model = hoopsWebViewer.model
    }
    leafNodesDatasource.current = getLeafNodeIds(hwv)
    nonLeafNodesDatasource.current = getNonLeafNodeIds(hwv)

    // Notify about initial mappings
    notifyMappingsReady()

    if (leafNodesDatasource.current.length > 1 && rootArticle && !isPollingChildren.current) {
      // The loaded 3D model contains an assembly -> fetch all available children
      pollPartChildren(rootArticle)
    }

    if (onModelStructureReady && hwv.current) {
      onModelStructureReady(hwv.current)
    }
  }

  const handleSelection = (selectionIds: Communicator.BodyId[]): void => {
    // Update selected nodes state
    setSelectedNodeIds(selectionIds)

    // Call external callback if provided
    if (onSelectionChange) {
      onSelectionChange(selectionIds)
    }

    log.debug('selectionIds ->', selectionIds)
  }

  const handleViewerSelection = (selections: Communicator.Event.NodeSelectionEvent[]): void => {
    if (selections.length > 0) {
      log.debug('Selection event:', selections)
      const selectionIds: number[] = selections.map(
        (selection: Communicator.Event.NodeSelectionEvent) => selection.getSelection().getNodeId()
      )
      // Sync model selection with ModelTree selection
      const currentModelTree: ModelTree | null = modelTree.current
      if (currentModelTree) {
        currentModelTree.selected = selectionIds
      }
      handleSelection(selectionIds)
    }
  }

  const handleViewerVisibilityChanged = (shownBodyIds: number[], hiddenBodyIds: number[]): void => {
    const currentModelTree: ModelTree | null = modelTree.current
    if (!currentModelTree) return
    shownBodyIds.forEach(nodeId => {
      currentModelTree.updateNodeData(nodeId, { visible: true })
      if (onVisibilityChange) {
        onVisibilityChange(nodeId, true)
      }
    })
    hiddenBodyIds.forEach(nodeId => {
      currentModelTree.updateNodeData(nodeId, { visible: false })
      if (onVisibilityChange) {
        onVisibilityChange(nodeId, false)
      }
    })
  }

  const handleWebGlContextLost = (): void => {
    toast.warning(t('request:feedback.warn.3dViewerCrashed'))
  }

  const handleTreeNodeClick = (event: ModelTreeNodeClickEvent): void => {
    const hoopsWebViewer: Communicator.WebViewer | null = hwv.current
    if (!hoopsWebViewer) throw new Error('Hoops Web Viewer is not defined')
    event.stopPropagation()
    hoopsWebViewer.selectionManager.clear(false)
    const nodeId: number = event.detail.nodeId as number
    hoopsWebViewer.selectionManager.selectNode(nodeId, Communicator.SelectionMode.Add)
    handleSelection([nodeId])
    if (onNodeClick) {
      onNodeClick(nodeId)
    }
  }

  const handleTreeNodeVisibilityChange = (event: ModelTreeNodeVisibilityClickEvent): void => {
    const hoopsWebViewer: Communicator.WebViewer | null = hwv.current
    const currentModelTree: ModelTree | null = modelTree.current
    if (!hoopsWebViewer || !currentModelTree)
      throw new Error('Hoops Web Viewer or Modeltree is not defined')
    event.stopPropagation()
    const nodeId: number = event.detail.nodeId as number
    const visibility: boolean = event.detail.visibility as boolean
    hoopsWebViewer.model.setNodesVisibility([nodeId], visibility)
    currentModelTree.updateNodeData(nodeId, {
      visible: visibility,
    })
    if (onVisibilityChange) {
      onVisibilityChange(nodeId, visibility)
    }
  }

  const handleSettingsChange = (key: string, checked: boolean): void => {
    setSettings(prev => {
      const i = prev.findIndex((k: string) => k === key)
      if (checked && i < 0) {
        return [...prev, key]
      } else if (!checked && i >= 0) {
        const newSettings = [...prev]
        newSettings.splice(i, 1)
        return newSettings
      }
      return prev
    })
  }
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  // Get file URL for scene ready handler
  const getFileUrl = (): string | null => {
    if (!file) return null
    if (typeof file === 'string') return file
    return file.document || null
  }

  // Create settings array for sidebar
  const createViewerSettings = (): ViewerSetting[] => {
    const hoopsWebViewer: Communicator.WebViewer | null = hwv.current
    if (!hoopsWebViewer) return []
    const view: View = hoopsWebViewer.view as View

    return [
      {
        label: t('cadFileViewer:interaction.checkbox.showEdges'),
        key: 'showEdges',
        onToggle: (checked: boolean) => {
          void (async () => {
            await view.setLineVisibility(checked)
          })()
          handleSettingsChange('showEdges', checked)
        },
      },
      {
        label: t('cadFileViewer:interaction.checkbox.ambientOcclusion'),
        key: 'ambientOcclusion',
        onToggle: (checked: boolean) => {
          void (async () => {
            await view.setAmbientOcclusionEnabled(checked)
          })()
          handleSettingsChange('ambientOcclusion', checked)
        },
      },
      {
        label: t('cadFileViewer:interaction.checkbox.antiAliasing'),
        key: 'antiAliasing',
        onToggle: (checked: boolean) => {
          void (async () => {
            await view.setAntiAliasingMode(
              checked ? Communicator.AntiAliasingMode.SMAA : Communicator.AntiAliasingMode.None
            )
          })()
          handleSettingsChange('antiAliasing', checked)
        },
      },
    ]
  }
  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */
  // Initialize HOOPS web viewer
  const fileUrl = getFileUrl()
  if (fileUrl && isDOMReady && hwv.current === null) {
    // File info is available -> initiate hoops web viewer
    hwv.current = new Communicator.WebViewer({
      containerId: containerId,
      enginePath: enginePath,
      empty: true, // The viewer will be started even when no model is available
      memoryLimit: 512, // Resource limit in MB. This prevents crashes caused by running out of memory
    })

    // Disable line and point picking (selection) in the selectionOperator
    const hoopsSelectionOperator = hwv.current.view.operatorManager.getOperator(Communicator.OperatorId.Select)
    const pickConfig = hoopsSelectionOperator.getPickConfig()
    pickConfig.allowPoints = false
    pickConfig.allowLines = false
    hoopsSelectionOperator.setPickConfig(pickConfig)

    // Set hoops web viewer callbacks
    hwv.current.setCallbacks({
      sceneReady: () => void handleSceneReady(fileUrl),
      modelLoadBegin: () => log.info('Begin loading model'),
      modelStructureReady: handleModelStructureReady,
      selectionArray: handleViewerSelection,
      visibilityChanged: handleViewerVisibilityChanged,
      modelLoadFailure: (error: any) => {
        log.error('Error loading model', error)
        toast.error(t('cadFileViewer:feedback.error.loading3DModel'))
      },
      webGlContextLost: handleWebGlContextLost,
    })

    hwv.current.start()
  }
  /* ------------------------- Pre render actions end ------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <Box display="flex" height="100%" width="100%">
      {showSidebar && (
        <Box width="25vw">
          <HoopsCADViewerSidebar
            modelTree={modelTree}
            modelName={modelName}
            settings={createViewerSettings()}
            settingsState={cadViewerSettingsState}
            onSettingsChange={handleSettingsChange}
            onNodeClick={handleTreeNodeClick}
            onVisibilityChange={handleTreeNodeVisibilityChange}
            openSettings={openCADViewerSettings}
            onToggleSettings={() => setOpenCADViewerSettings(prev => !prev)}
            webViewer={hwv}
          />
        </Box>
      )}
      <Box flexGrow={1} position="relative" overflow="hidden">
        <Box
          id={containerId + '-container'}
          height="100%"
          width="100%"
          position="relative"
          overflow="hidden"
        >
          {!isViewerReady && (
            <Box
              display="flex"
              height="100%"
              width="100%"
              justifyContent="center"
              alignItems="center"
            >
              <CircularProgress />
            </Box>
          )}
          <Box id={containerId}></Box>
          {isViewerReady && showToolbar && (
            <HoopsCADViewerToolbar webViewer={hwv} position={toolbarPosition} />
          )}
        </Box>
      </Box>
    </Box>
  )
}
